import { listingBusinessId } from './analytics-content.mjs'

export const ANALYTICS_SCHEMA_VERSION = '1.0.0'
export const ANALYTICS_COLLECTOR_VERSION = '1.0.0'
export const ANALYTICS_SITE_ID = 'selong_guide'
export const ANALYTICS_SITE_ROLE = 'destination_guide'

const ALLOWED_QUERY_PARAMETERS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_content',
  'utm_term',
])

const PROHIBITED_KEYS = new Set([
  'name',
  'first_name',
  'last_name',
  'email',
  'phone',
  'telephone',
  'special_requests',
  'booking_reference',
  'access_token',
  'session_token',
  'payment_url',
  'ip',
  'ip_address',
  'raw_ip',
])

const PAGE_RULES = [
  {
    match: /^\/$/,
    values: ['destination_home', 'explore', 'destination_research', 'selong_belanak_destination', 'informational'],
  },
  {
    match: /^\/blog\/?$/,
    values: ['article_index', 'explore', 'destination_research', 'selong_belanak_destination', 'informational'],
  },
  {
    match: /^\/blog\//,
    values: ['article_detail', 'explore', 'destination_research', 'selong_belanak_destination', 'informational'],
  },
  {
    match: /^\/where-to-stay\//,
    values: ['listing_detail', 'compare', 'accommodation_research', 'where_to_stay', 'commercial_investigation'],
  },
  {
    match: /^\/where-to-stay\/?$/,
    values: ['listing_directory', 'compare', 'accommodation_research', 'where_to_stay', 'commercial_investigation'],
  },
  {
    match: /^\/eat-drink\//,
    values: ['listing_detail', 'compare', 'dining_research', 'restaurants_and_bars', 'commercial_investigation'],
  },
  {
    match: /^\/eat-drink\/?$/,
    values: ['listing_directory', 'explore', 'dining_research', 'restaurants_and_bars', 'commercial_investigation'],
  },
  {
    match: /^\/surf\//,
    values: ['listing_detail', 'explore', 'surf_research', 'surfing', 'commercial_investigation'],
  },
  {
    match: /^\/surf\/?$/,
    values: ['listing_directory', 'explore', 'surf_research', 'surfing', 'commercial_investigation'],
  },
  {
    match: /^\/beaches-surfing/,
    values: ['topic_hub', 'explore', 'beach_research', 'beaches', 'informational'],
  },
  {
    match: /^\/things-to-do/,
    values: ['topic_hub', 'explore', 'activity_research', 'things_to_do', 'commercial_investigation'],
  },
  {
    match: /^\/events-festivals/,
    values: ['topic_hub', 'explore', 'event_research', 'events_and_festivals', 'informational'],
  },
  {
    match: /^\/getting-to-selong-belanak/,
    values: ['practical_guide', 'consider', 'transport_planning', 'getting_here', 'informational'],
  },
  {
    match: /^\/getting-around/,
    values: ['practical_guide', 'consider', 'transport_planning', 'getting_around', 'informational'],
  },
  {
    match: /^\/travel-tips/,
    values: ['practical_guide', 'consider', 'practical_planning', 'travel_tips', 'informational'],
  },
  {
    match: /^\/explore/,
    values: ['map_explorer', 'explore', 'destination_research', 'selong_belanak_destination', 'informational'],
  },
  {
    match: /^\/sbca/,
    values: ['community_page', 'explore', 'community_interest', 'community_and_sbca', 'informational'],
  },
  {
    match: /^\/contact/,
    values: ['contact_page', 'consider', 'contact_intent', 'selong_belanak_destination', 'navigational'],
  },
  {
    match: /^\/(privacy-policy|editorial-policy)/,
    values: ['policy_page', 'explore', 'policy_information', 'selong_belanak_destination', 'navigational'],
  },
  {
    match: /^\/about/,
    values: ['about_page', 'explore', 'destination_research', 'selong_belanak_destination', 'navigational'],
  },
  {
    match: /^\/404/,
    values: ['error_page', 'explore', 'unknown', 'selong_belanak_destination', 'informational'],
  },
]

const OVERRIDE_FIELDS = {
  pageType: 'page_type',
  funnelStage: 'funnel_stage',
  intentClass: 'intent_class',
  primaryTopic: 'primary_topic',
  searchIntent: 'search_intent',
}

const randomId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const cleanObject = (value) => {
  if (Array.isArray(value)) {
    return value.map(cleanObject).filter((item) => item !== undefined)
  }
  if (!value || typeof value !== 'object') return value

  return Object.entries(value).reduce((result, [key, item]) => {
    if (PROHIBITED_KEYS.has(key.toLowerCase())) return result
    const cleaned = cleanObject(item)
    if (cleaned !== undefined && cleaned !== '' && cleaned !== null) {
      result[key] = cleaned
    }
    return result
  }, {})
}

export function sanitizeAnalyticsUrl(input, base = 'https://selong-belanak.com') {
  let parsed
  try {
    parsed = new URL(input, base)
  } catch {
    return { url: '', gclid_present: false, msclkid_present: false }
  }

  const gclidPresent = parsed.searchParams.has('gclid')
  const msclkidPresent = parsed.searchParams.has('msclkid')
  const kept = new URLSearchParams()
  for (const [key, value] of parsed.searchParams.entries()) {
    if (ALLOWED_QUERY_PARAMETERS.has(key.toLowerCase())) kept.append(key.toLowerCase(), value)
  }
  parsed.search = kept.toString()
  parsed.hash = ''

  return {
    url: parsed.toString(),
    gclid_present: gclidPresent,
    msclkid_present: msclkidPresent,
  }
}

const normaliseDomain = (hostname = '') => hostname.toLowerCase().replace(/^www\./, '')

const NON_SEARCH_GOOGLE_PATH_PREFIXES = [
  '/finance',
  '/flights',
  '/hotels',
  '/maps',
  '/shopping',
  '/travel',
]

const isGoogleSearchDomain = (hostname = '') =>
  /^google\.(?:[a-z]{2,3}|(?:com|co)\.[a-z]{2})$/i.test(normaliseDomain(hostname))

const isGoogleSearchReferrer = (referrer) => {
  if (!referrer || !isGoogleSearchDomain(referrer.hostname)) return false
  const pathname = referrer.pathname.toLowerCase()
  return !NON_SEARCH_GOOGLE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

const normaliseFirstTouch = (firstTouch) => {
  if (
    firstTouch?.medium === 'referral' &&
    isGoogleSearchDomain(firstTouch.source)
  ) {
    return { ...firstTouch, source: 'google', medium: 'organic' }
  }
  return firstTouch
}

export function deriveAcquisitionContext({
  currentUrl = '',
  referrerUrl = '',
  storedFirstTouch = null,
} = {}) {
  let current
  let referrer
  try {
    current = new URL(currentUrl, 'https://selong-belanak.com')
  } catch {
    current = new URL('https://selong-belanak.com')
  }
  try {
    referrer = referrerUrl ? new URL(referrerUrl) : null
  } catch {
    referrer = null
  }

  const sourceFromUtm = current.searchParams.get('utm_source') || ''
  const mediumFromUtm = current.searchParams.get('utm_medium') || ''
  const campaign = current.searchParams.get('utm_campaign') || ''
  const referrerDomain = normaliseDomain(referrer?.hostname)
  const currentDomain = normaliseDomain(current.hostname)
  const externalReferrer = referrerDomain && referrerDomain !== currentDomain
  const googleOrganic =
    !sourceFromUtm && !mediumFromUtm && externalReferrer && isGoogleSearchReferrer(referrer)
  const source = sourceFromUtm || (googleOrganic ? 'google' : externalReferrer ? referrerDomain : 'direct')
  const medium = mediumFromUtm || (googleOrganic ? 'organic' : externalReferrer ? 'referral' : 'none')
  const landingPageUrl = sanitizeAnalyticsUrl(current.toString()).url
  const sanitisedReferrer = referrer ? sanitizeAnalyticsUrl(referrer.toString()).url : ''
  const firstTouch = normaliseFirstTouch(storedFirstTouch) || {
    source,
    medium,
    campaign,
    landing_page_url: landingPageUrl,
  }

  return compact({
    landing_page_url: landingPageUrl,
    referrer_url: sanitisedReferrer,
    referrer_domain: externalReferrer ? referrerDomain : undefined,
    source,
    medium,
    campaign,
    campaign_id: current.searchParams.get('utm_id') || undefined,
    content: current.searchParams.get('utm_content') || undefined,
    term: current.searchParams.get('utm_term') || undefined,
    first_touch_source: firstTouch.source,
    first_touch_medium: firstTouch.medium,
    first_touch_campaign: firstTouch.campaign,
    first_touch_landing_page_url: firstTouch.landing_page_url,
    latest_touch_source: source,
    latest_touch_medium: medium,
    latest_touch_campaign: campaign,
  })
}

export function classifyPage(inputPath = '/', overrides = {}) {
  const path = inputPath.split('?')[0].split('#')[0] || '/'
  const rule = PAGE_RULES.find(({ match }) => match.test(path))
  const [pageType, funnelStage, intentClass, primaryTopic, searchIntent] =
    rule?.values || ['content_page', 'explore', 'destination_research', 'selong_belanak_destination', 'informational']

  const result = {
    page_type: pageType,
    funnel_stage: funnelStage,
    intent_class: intentClass,
    primary_topic: primaryTopic,
    search_intent: searchIntent,
  }

  for (const [source, destination] of Object.entries(OVERRIDE_FIELDS)) {
    if (typeof overrides?.[source] === 'string' && overrides[source].trim()) {
      result[destination] = overrides[source].trim()
    }
  }
  return result
}

const normaliseContentId = (type, id) =>
  id ? `${ANALYTICS_SITE_ID}:${type}:${String(id).replace(/^drafts\./, '')}` : undefined

export function derivePageContext({ path = '/', pageUrl = '', pageTitle = '', pageProps = {} } = {}) {
  const rawDocument = pageProps.listing || pageProps.post || null
  const suppliedContent =
    pageProps.content || pageProps.data?.content || pageProps.page?.content || {}
  const analytics = rawDocument?.analytics || suppliedContent.analytics || suppliedContent || {}
  const taxonomy = classifyPage(path, analytics)
  const sanitised = sanitizeAnalyticsUrl(pageUrl || path)
  let canonicalUrl = sanitised.url
  try {
    const parsedCanonical = new URL(sanitised.url)
    parsedCanonical.search = ''
    parsedCanonical.hash = ''
    canonicalUrl = parsedCanonical.toString()
  } catch {
    canonicalUrl = sanitised.url.split('?')[0].split('#')[0]
  }

  const isListing = Boolean(rawDocument?.sectionPage || rawDocument?.location)
  const isArticle = Boolean(rawDocument && !isListing && (rawDocument.sections || rawDocument._type === 'blogPost'))
  const contentType = suppliedContent.content_type || (isListing ? 'listing' : isArticle ? 'article' : 'page')
  const rawContentId = suppliedContent.sanity_id || suppliedContent._id || rawDocument?._id
  const contentId = suppliedContent.content_id || normaliseContentId(contentType, rawContentId)
  const businessId = analytics.businessId || suppliedContent.business_id || (isListing ? listingBusinessId(rawDocument) : undefined)
  const contentTitle =
    suppliedContent.content_title || rawDocument?.location || rawDocument?.title || pageTitle

  return compact({
    ...taxonomy,
    page_url: sanitised.url,
    canonical_url: canonicalUrl,
    page_path: path.split('?')[0].split('#')[0] || '/',
    page_title: pageTitle,
    gclid_present: sanitised.gclid_present,
    msclkid_present: sanitised.msclkid_present,
    content_id: contentId,
    content_revision: suppliedContent.content_revision || suppliedContent._rev || rawDocument?._rev,
    content_type: contentType,
    content_title: contentTitle,
    content_slug:
      suppliedContent.content_slug || rawDocument?.slug?.current || rawDocument?.slug,
    secondary_topics: analytics.secondaryTopics || suppliedContent.secondary_topics,
    audience_segment:
      analytics.audienceSegments?.[0] || suppliedContent.audience_segment,
    listing_id: isListing ? contentId : suppliedContent.listing_id,
    business_id: businessId,
    business_name: isListing ? rawDocument?.location : suppliedContent.business_name,
    business_category: isListing ? rawDocument?.category : suppliedContent.business_category,
    business_area: isListing ? rawDocument?.area : suppliedContent.business_area,
    business_section: isListing ? rawDocument?.sectionPage : suppliedContent.business_section,
    editors_pick: isListing ? Boolean(rawDocument?.editorsPick) : suppliedContent.editors_pick,
  })
}

export function classifyLink({ href = '', text = '', currentHost = 'selong-belanak.com' } = {}) {
  if (/^tel:/i.test(href)) return 'phone'
  if (/^mailto:/i.test(href)) return 'email'

  let url
  try {
    url = new URL(href, `https://${currentHost}`)
  } catch {
    return 'unknown'
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  const normalizedCurrentHost = currentHost.toLowerCase().replace(/^www\./, '')
  const label = text.trim().toLowerCase()
  if (['wa.me', 'api.whatsapp.com', 'web.whatsapp.com'].some((domain) => host === domain || host.endsWith(`.${domain}`))) {
    return 'whatsapp'
  }
  if (
    host === 'rm.bonibeach.com' ||
    /\b(book|booking|reserve|reservation)\b/.test(label) ||
    /\/(book|booking|reserve|reservation)(\/|$)/i.test(url.pathname)
  ) {
    return 'booking'
  }
  if (host === 'bonibeach.com' || host.endsWith('.bonibeach.com')) return 'owned_site_handoff'
  if (host === normalizedCurrentHost || host.endsWith(`.${normalizedCurrentHost}`)) return 'internal'
  return 'outbound'
}

export function buildCanonicalEvent({
  eventName,
  context = {},
  properties = {},
  identity = {},
  consentState = 'unknown',
  eventId = randomId(),
  timestamp = new Date().toISOString(),
  environment = process.env.NODE_ENV === 'production' ? 'production' : 'development',
} = {}) {
  if (!eventName || typeof eventName !== 'string') return null

  return cleanObject({
    event_id: eventId,
    event_name: eventName,
    event_timestamp: timestamp,
    schema_version: ANALYTICS_SCHEMA_VERSION,
    collector_version: ANALYTICS_COLLECTOR_VERSION,
    site_id: ANALYTICS_SITE_ID,
    site_role: ANALYTICS_SITE_ROLE,
    environment,
    consent_state: consentState,
    actor_class: 'human',
    is_internal_traffic: false,
    ...context,
    ...identity,
    ...properties,
  })
}

const compact = (value) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
  )

const gaItem = (event) =>
  compact({
    item_id: event.business_id || event.listing_id || event.room_type_id,
    item_name: event.business_name || event.content_title || event.room_type_name,
    item_category: event.business_category,
    index: event.listing_position,
  })

export function mapCanonicalEventToGA(event = {}) {
  const common = compact({
    page_path: event.page_path,
    page_location: event.page_url,
    page_title: event.page_title,
    page_type: event.page_type,
    funnel_stage: event.funnel_stage,
    intent_class: event.intent_class,
    primary_topic: event.primary_topic,
    content_id: event.content_id,
    content_type: event.content_type,
    business_id: event.business_id,
    business_name: event.business_name,
    business_category: event.business_category,
    business_area: event.business_area,
    business_section: event.business_section,
    editors_pick: event.editors_pick,
    module_id: event.module_id,
    module_type: event.module_type,
    placement_id: event.placement_id,
    cta_id: event.cta_id,
    cta_type: event.cta_type,
    link_url: event.destination_url,
    link_domain: event.destination_domain,
    destination_site_id: event.destination_site_id,
  })

  if (event.event_name === 'page_viewed') {
    return {
      eventName: 'page_view',
      parameters: compact({
        page_title: event.page_title,
        page_location: event.page_url,
        ...common,
      }),
    }
  }

  if (event.event_name === 'listing_impression') {
    const listId = event.primary_topic || event.business_section || 'listings'
    return {
      eventName: 'view_item_list',
      parameters: compact({
        item_list_id: listId,
        item_list_name: listId,
        items: [gaItem(event)],
        page_path: event.page_path,
        page_location: event.page_url,
        page_title: event.page_title,
      }),
    }
  }

  if (event.event_name === 'listing_selected') {
    const listId = event.primary_topic || event.business_section || 'listings'
    return {
      eventName: 'select_item',
      parameters: compact({
        item_list_id: listId,
        item_list_name: listId,
        items: [gaItem(event)],
        page_path: event.page_path,
        page_location: event.page_url,
        page_title: event.page_title,
      }),
    }
  }

  if (event.event_name === 'listing_viewed') {
    return { eventName: 'view_item', parameters: { items: [gaItem(event)], ...common } }
  }

  if (event.event_name === 'cta_selected') {
    const eventNameByType = {
      booking: 'booking_click',
      whatsapp: 'whatsapp_click',
      phone: 'phone_click',
      email: 'email_click',
      website: 'website_click',
      directions: 'directions_click',
      menu: 'menu_click',
    }
    return { eventName: eventNameByType[event.cta_type] || 'cta_click', parameters: common }
  }

  if (event.event_name === 'contact_selected') {
    return {
      eventName: `${event.cta_type || 'contact'}_click`,
      parameters: common,
    }
  }

  return {
    eventName: event.event_name,
    parameters: compact({
      ...common,
      scroll_percent: event.scroll_percent,
      engagement_threshold_seconds: event.engagement_threshold_seconds,
      engagement_time_msec: event.engagement_milliseconds,
      filter_type: event.filter_type,
      filter_value: event.filter_value,
      form_id: event.form_id,
      interaction_type: event.interaction_type,
      photo_id: event.photo_id,
      photo_position: event.photo_position,
      photo_count: event.photo_count,
      error_type: event.error_type,
    }),
  }
}

export function containsProhibitedAnalyticsKey(value) {
  if (Array.isArray(value)) return value.some(containsProhibitedAnalyticsKey)
  if (!value || typeof value !== 'object') return false
  return Object.entries(value).some(
    ([key, item]) => PROHIBITED_KEYS.has(key.toLowerCase()) || containsProhibitedAnalyticsKey(item)
  )
}
